import { NAMESPACES, NS, type NamespaceTuple } from '@ownyou/shared-types';
import type { DataSourceId, DataSourceProvider, DataSourceType } from '../../contexts/DataSourceContext';
import {
  createMissionSpineRunId,
  recordMissionSpineMarker,
} from '../mission-spine/instrumentation';
import { writeConnectedSourceAuthOutcome } from '../source-read-model/read-model';
import { validateWelcomeOutcomeBodySafety, type WelcomeOutcomeRecord } from '../welcome/outcomes';
import slice08Contract from './slice08-hitl-contract.json';
import {
  getDatabaseNames,
  getMemoryRecords,
  openDb,
  type StoredMemoryRecord,
} from './hitl-browser-store';
import type { SafeRecord } from './hitl-core';

interface StoreWriteLike {
  put<T>(namespace: NamespaceTuple, key: string, value: T): Promise<void>;
}

interface Slice08SandboxSourceMetadata {
  provider: DataSourceProvider;
  type: DataSourceType;
  namespaceName: string;
  namespace: (userId: string) => NamespaceTuple;
  reasonCode: string;
}

const SLICE08_SOURCE_METADATA: Record<DataSourceId, Slice08SandboxSourceMetadata> = {
  gmail: {
    provider: 'google',
    type: 'email',
    namespaceName: NAMESPACES.IAB_CLASSIFICATIONS,
    namespace: NS.iabClassifications,
    reasonCode: 'email-sync-complete',
  },
  outlook: {
    provider: 'microsoft',
    type: 'email',
    namespaceName: NAMESPACES.IAB_CLASSIFICATIONS,
    namespace: NS.iabClassifications,
    reasonCode: 'email-sync-complete',
  },
  'google-calendar': {
    provider: 'google',
    type: 'calendar',
    namespaceName: NAMESPACES.CALENDAR_EVENTS,
    namespace: NS.calendarEvents,
    reasonCode: 'calendar-sync-complete',
  },
  'microsoft-calendar': {
    provider: 'microsoft',
    type: 'calendar',
    namespaceName: NAMESPACES.CALENDAR_EVENTS,
    namespace: NS.calendarEvents,
    reasonCode: 'calendar-sync-complete',
  },
  plaid: {
    provider: 'plaid',
    type: 'financial',
    namespaceName: NAMESPACES.FINANCIAL_TRANSACTIONS,
    namespace: NS.financialTransactions,
    reasonCode: 'financial-sync-complete',
  },
  'chrome-history': {
    provider: 'browser-extension',
    type: 'browsing',
    namespaceName: NAMESPACES.BROWSING_HISTORY,
    namespace: NS.browsingHistory,
    reasonCode: 'browsing-sync-complete',
  },
};

const SOURCE_WRITE_NAMESPACES = [
  NAMESPACES.IAB_CLASSIFICATIONS,
  NAMESPACES.CALENDAR_EVENTS,
  NAMESPACES.FINANCIAL_TRANSACTIONS,
  NAMESPACES.BROWSING_HISTORY,
] as const;

const MISSION_READINESS_NAMESPACES = [
  NAMESPACES.MISSION_CARDS,
  NAMESPACES.IKIGAI_OPPORTUNITIES,
  NAMESPACES.PIPELINE_METADATA,
] as const;

export async function writeSlice08SandboxSourceEvidence(input: {
  store: StoreWriteLike;
  userId: string;
  sourceId: DataSourceId;
  now?: Date;
}): Promise<void> {
  const metadata = SLICE08_SOURCE_METADATA[input.sourceId];
  const observedAt = input.now ?? new Date();
  const isoObservedAt = observedAt.toISOString();
  const key = `${slice08Contract.sandbox.sourceWriteKeyPrefix}-${input.sourceId}`;

  await writeConnectedSourceAuthOutcome({
    store: input.store,
    userId: input.userId,
    sourceId: input.sourceId,
    now: observedAt.getTime(),
  });
  await input.store.put(NS.uiPreferences(input.userId), `sync_${input.sourceId}`, {
    itemCount: 1,
    lastSync: isoObservedAt,
  });
  await input.store.put(metadata.namespace(input.userId), key, {
    id: key,
    summary: slice08Contract.sandbox.sourceWriteSummary,
    sourceType: metadata.type,
    provider: metadata.provider,
    timestamp: isoObservedAt,
    metadata: {
      bodySafe: true,
      marker: slice08Contract.sandbox.sourceWriteMarker,
    },
  });

  const runId = createMissionSpineRunId(`source-${input.sourceId}`);
  recordMissionSpineMarker({
    runId,
    hop: 'source_write',
    status: 'success',
    namespace: metadata.namespaceName,
    sourceId: input.sourceId,
    reasonCode: metadata.reasonCode,
    counts: { itemCount: 1 },
  });

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('ownyou:data-changed', {
      detail: { namespace: metadata.namespaceName, sourceId: input.sourceId, runId },
    }));
    window.dispatchEvent(new CustomEvent('data-changed', {
      detail: { source: metadata.type },
    }));
  }
}

interface Slice08StoreTarget {
  dbName: string;
  userId: string;
  rows: StoredMemoryRecord[];
}

function subKey(row: StoredMemoryRecord): string {
  return row.key.split('::').slice(2).join('::');
}

function safePrefix(value: string | undefined): string | undefined {
  if (!value) return undefined;
  return value.slice(0, 10);
}

function safeOutcome(value: unknown): SafeRecord | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const outcome = value as WelcomeOutcomeRecord;
  const violations = validateWelcomeOutcomeBodySafety(outcome);
  return {
    schemaVersion: outcome.schemaVersion,
    state: outcome.state,
    reasonCode: typeof outcome.reasonCode === 'string' ? outcome.reasonCode : undefined,
    sourceId: outcome.sourceId,
    routeId: outcome.routeId,
    nextRoute: outcome.nextRoute,
    evidenceRefCount: Array.isArray(outcome.evidenceRefs) ? outcome.evidenceRefs.length : 0,
    createdAt: outcome.createdAt,
    updatedAt: outcome.updatedAt,
    bodySafetyViolationCount: violations.length,
  };
}

function countByNamespace(rows: StoredMemoryRecord[], namespaces: readonly string[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const namespace of namespaces) {
    counts[namespace] = rows.filter(row => row.namespace === namespace).length;
  }
  return counts;
}

function scoreCandidate(rows: StoredMemoryRecord[], userId: string): number {
  const userRows = rows.filter(row => row.userId === userId);
  const outcomeCount = userRows.filter(row => row.namespace === NAMESPACES.WELCOME_OUTCOMES).length;
  const sourceAuthCount = userRows.filter(row => row.namespace === NAMESPACES.UI_PREFERENCES && subKey(row).startsWith('source_auth_')).length;
  const syncCount = userRows.filter(row => row.namespace === NAMESPACES.UI_PREFERENCES && subKey(row).startsWith('sync_')).length;
  const sourceWriteCount = userRows.filter(row => SOURCE_WRITE_NAMESPACES.includes(row.namespace as typeof SOURCE_WRITE_NAMESPACES[number])).length;
  const missionCount = userRows.filter(row => MISSION_READINESS_NAMESPACES.includes(row.namespace as typeof MISSION_READINESS_NAMESPACES[number])).length;
  return (outcomeCount * 1_000) + (sourceAuthCount * 100) + (syncCount * 50) + (sourceWriteCount * 10) + missionCount + userRows.length;
}

async function chooseSlice08StoreTarget(): Promise<Slice08StoreTarget> {
  const dbNames = await getDatabaseNames();
  const candidates: Array<Slice08StoreTarget & { score: number }> = [];

  for (const dbName of dbNames) {
    const db = await openDb(dbName);
    try {
      if (!db.objectStoreNames.contains('memories')) continue;
      const rows = await getMemoryRecords(db);
      const userIds = [...new Set(rows.map(row => row.userId).filter(Boolean))];
      for (const userId of userIds) {
        const score = scoreCandidate(rows, userId);
        if (score > 0) candidates.push({ dbName, userId, rows, score });
      }
    } finally {
      db.close();
    }
  }

  candidates.sort((a, b) => b.score - a.score);
  const target = candidates[0];
  if (!target) {
    throw new Error('Slice 08 Store inspection needs an authenticated OwnYou IndexedDB with Store rows.');
  }

  return target;
}

export async function readSlice08StoreSummary(): Promise<SafeRecord> {
  const target = await chooseSlice08StoreTarget();
  const rows = target.rows.filter(row => row.userId === target.userId);
  const outcomeRows = rows.filter(row => row.namespace === NAMESPACES.WELCOME_OUTCOMES);
  const outcomes = outcomeRows
    .map(row => safeOutcome(row.value))
    .filter((outcome): outcome is SafeRecord => Boolean(outcome))
    .sort((a, b) => Number(b.updatedAt ?? 0) - Number(a.updatedAt ?? 0));
  const uiRows = rows.filter(row => row.namespace === NAMESPACES.UI_PREFERENCES);
  const sourceAuthRows = uiRows.filter(row => subKey(row).startsWith('source_auth_'));
  const oauthRows = uiRows.filter(row => subKey(row).startsWith('oauth_'));
  const syncRows = uiRows.filter(row => subKey(row).startsWith('sync_'));
  const sourceWriteCounts = countByNamespace(rows, SOURCE_WRITE_NAMESPACES);
  const missionReadinessCounts = countByNamespace(rows, MISSION_READINESS_NAMESPACES);

  return {
    observedAt: new Date().toISOString(),
    target: {
      dbName: target.dbName,
      userIdPrefix: safePrefix(target.userId),
    },
    welcomeOutcomes: {
      count: outcomes.length,
      states: [...new Set(outcomes.map(outcome => String(outcome.state)))].sort(),
      latest: outcomes[0],
      bodySafetyViolationCount: outcomes.reduce((sum, outcome) => sum + Number(outcome.bodySafetyViolationCount ?? 0), 0),
    },
    sourceAuth: {
      sourceAuthOutcomeCount: sourceAuthRows.length,
      sourceAuthSourceIds: sourceAuthRows.map(row => subKey(row).replace(/^source_auth_/, '')).sort(),
      oauthMetadataRowCount: oauthRows.length,
      oauthSourceIds: oauthRows.map(row => subKey(row).replace(/^oauth_/, '')).sort(),
      syncMetadataRowCount: syncRows.length,
      syncSourceIds: syncRows.map(row => subKey(row).replace(/^sync_/, '')).sort(),
    },
    sourceWrites: {
      namespaceCounts: sourceWriteCounts,
      totalCount: Object.values(sourceWriteCounts).reduce((sum, count) => sum + count, 0),
      hasSourceWriteEvidence: Object.values(sourceWriteCounts).some(count => count > 0),
    },
    missionReadiness: {
      namespaceCounts: missionReadinessCounts,
      totalCount: Object.values(missionReadinessCounts).reduce((sum, count) => sum + count, 0),
    },
  };
}
